'use client';

/**
 * SHARED LANDING PAGE RENDERER
 *
 * This is the SINGLE SOURCE OF TRUTH for rendering landing pages.
 * Used by:
 * - Dashboard preview (AiGeneratedPreview) with isEditing=true
 * - Public site (/site/[subdomain]) with isEditing=false
 *
 * Any styling changes here will be reflected in BOTH places.
 */

import React from 'react';
import type { AIResponse, Section } from '@/features/projects/types';
import { NavbarConfig, FormConfig } from './types';
export type { NavbarConfig, FormConfig };

import { FONT_SIZE_CLASSES } from './constants';
import { parseTypographySettings, generateFullTypographyCSS } from '@/constants/fonts';

// Import extracted sections
import { NavbarSection } from './renderer-sections/NavbarSection';
import { HeroSection } from './renderer-sections/HeroSection';
import { FeaturesSection } from './renderer-sections/FeaturesSection';
import { TestimonialsSection } from './renderer-sections/TestimonialsSection';
import { FAQSection } from './renderer-sections/FAQSection';
import { EmailSignupSection } from './renderer-sections/EmailSignupSection';
import { FinalCTASection } from './renderer-sections/FinalCTASection';
import { HowItWorksSection } from './renderer-sections/HowItWorksSection';
import { PricingSection } from './renderer-sections/PricingSection';
import { TeamSection } from './renderer-sections/TeamSection';
import { FooterSection } from './renderer-sections/FooterSection';

export interface LandingPageRendererProps {
    content: AIResponse;
    visibleSections: Section[];
    primaryColor: string;
    fontFamily?: string;
    fontSize?: 'small' | 'medium' | 'large' | 'xlarge';
    productName?: string;
    navbarConfig?: NavbarConfig;
    formConfig?: FormConfig;
    logoUrl?: string | null;
    buttonStyles?: {
        navbar?: string;
        hero?: string;
        finalCta?: string;
    };
    badgeText?: string | null;
    showBadge?: boolean;
    badgePosition?: 'navbar' | 'hero';
    isEditing?: boolean;
    onContentEdit?: (section: string, field: string, value: string, index?: number) => void;
    onEmailSubmit?: (data: { email: string; firstName?: string; lastName?: string; phone?: string; customAnswer?: string }) => Promise<{ success: boolean; data?: any }>;
    onCtaClick?: (source: 'hero' | 'finalCta') => void;
    onArrayAction?: (action: 'add' | 'delete', section: 'features' | 'faq', index?: number) => void;
}

// Default navbar config
const defaultNavbarConfig: NavbarConfig = {
    menuItems: [
        { id: 'nav-1', label: 'Features', targetSection: 'features' },
        { id: 'nav-2', label: 'Testimonials', targetSection: 'testimonials' },
        { id: 'nav-3', label: 'FAQ', targetSection: 'faq' },
        { id: 'nav-4', label: 'Pricing', targetSection: 'pricing' },
    ],
    ctaText: 'Get Started',
};

// Default form config
const defaultFormConfig: FormConfig = {
    emailPrompt: 'Join Our Waitlist',
    emailButtonText: 'Join Waitlist',
    collectFirstName: true,
    collectLastName: false,
    collectPhone: false,
    customQuestion: null,
};

export default function LandingPageRenderer({
    content,
    visibleSections,
    primaryColor = '#2563EB',
    fontFamily = 'Inter',
    fontSize = 'medium',
    productName = 'Your Product',
    navbarConfig = defaultNavbarConfig,
    formConfig = defaultFormConfig,
    logoUrl,
    buttonStyles,
    badgeText,
    showBadge,
    badgePosition = 'navbar',
    isEditing = false,
    onContentEdit,
    onEmailSubmit,
    onCtaClick,
    onArrayAction,
}: LandingPageRendererProps) {

    // Parse typography (supports both legacy and new format)
    const typography = parseTypographySettings(fontFamily, fontSize);

    // Generate sizeClasses: custom px-based or Tailwind preset
    const sizeClasses = typography.customSizes
        ? { heading: 'typo-heading', subheading: 'typo-subheading', body: 'typo-body', small: 'typo-small' }
        : (FONT_SIZE_CLASSES[fontSize] || FONT_SIZE_CLASSES.medium);

    const handleEdit = (section: string, field: string, value: string, index?: number) => {
        if (isEditing && onContentEdit) {
            onContentEdit(section, field, value, index);
        }
    };

    const renderSection = (section: Section) => {
        switch (section.type) {
            case 'navbar':
                return (
                    <NavbarSection
                        key="navbar"
                        productName={productName}
                        primaryColor={primaryColor}
                        navbarConfig={navbarConfig}
                        logoUrl={logoUrl}
                        buttonStyle={buttonStyles?.navbar as any}
                        badgeText={badgeText}
                        showBadge={showBadge && badgePosition === 'navbar'}
                        layout={section.layout as any}
                        layoutConfig={section.layoutConfig}
                    />
                );

            case 'hero':
                return (
                    <HeroSection
                        key="hero"
                        content={content.hero}
                        primaryColor={primaryColor}
                        sizeClasses={sizeClasses}
                        isEditing={isEditing}
                        onEdit={handleEdit}
                        layout={section.layout as any}
                        layoutConfig={section.layoutConfig}
                        buttonStyle={buttonStyles?.hero as any}
                        badgeText={badgeText}
                        showBadge={showBadge && badgePosition === 'hero'}
                        onCtaClick={onCtaClick}
                        formConfig={formConfig}
                        onSubmit={onEmailSubmit}
                    />
                );

            case 'features':
                return content.features?.length > 0 ? (
                    <FeaturesSection
                        key="features"
                        features={content.features}
                        primaryColor={primaryColor}
                        sizeClasses={sizeClasses}
                        isEditing={isEditing}
                        onEdit={handleEdit}
                        layout={section.layout as any}
                        layoutConfig={section.layoutConfig}
                        onArrayAction={onArrayAction}
                    />
                ) : null;

            case 'testimonials':
                return content.testimonials?.items?.length > 0 ? (
                    <TestimonialsSection
                        key="testimonials"
                        testimonials={content.testimonials.items}
                        title={content.testimonials.title}
                        primaryColor={primaryColor}
                        sizeClasses={sizeClasses}
                        isEditing={isEditing}
                        onEdit={handleEdit}
                        layout={section.layout as any}
                        layoutConfig={section.layoutConfig}
                    />
                ) : null;

            case 'faq':
                return content.faq?.items?.length > 0 ? (
                    <FAQSection
                        key="faq"
                        faqs={content.faq.items}
                        title={content.faq.title}
                        primaryColor={primaryColor}
                        sizeClasses={sizeClasses}
                        isEditing={isEditing}
                        onEdit={handleEdit}
                        layout={section.layout as any}
                        onArrayAction={onArrayAction}
                    />
                ) : null;

            case 'emailSignup':
                return (
                    <EmailSignupSection
                        key="emailSignup"
                        primaryColor={primaryColor}
                        formConfig={formConfig}
                        sizeClasses={sizeClasses}
                        onSubmit={onEmailSubmit}
                        layout={section.layout as any}
                        layoutConfig={section.layoutConfig}
                    />
                );

            case 'finalCta':
                return content.finalCta ? (
                    <FinalCTASection
                        key="finalCta"
                        content={content.finalCta}
                        primaryColor={primaryColor}
                        sizeClasses={sizeClasses}
                        isEditing={isEditing}
                        onEdit={handleEdit}
                        buttonStyle={buttonStyles?.finalCta as any}
                        onCtaClick={onCtaClick}
                    />
                ) : null;

            case 'howItWorks':
                return (
                    <HowItWorksSection
                        key="howItWorks"
                        primaryColor={primaryColor}
                        sizeClasses={sizeClasses}
                        isEditing={isEditing}
                        content={content.howItWorks}
                    />
                );

            case 'pricing':
                return (
                    <PricingSection
                        key="pricing"
                        primaryColor={primaryColor}
                        sizeClasses={sizeClasses}
                        isEditing={isEditing}
                    />
                );

            case 'team':
                return (
                    <TeamSection
                        key="team"
                        primaryColor={primaryColor}
                        sizeClasses={sizeClasses}
                        isEditing={isEditing}
                    />
                );

            case 'footer':
                const navbarSec = visibleSections.find(s => s.type === 'navbar');
                return (
                    <FooterSection
                        key="footer"
                        productName={productName}
                        primaryColor={primaryColor}
                        logoUrl={logoUrl}
                        layout={section.layout as any}
                        layoutConfig={section.layoutConfig}
                        navbarLayoutConfig={navbarSec?.layoutConfig}
                    />
                );

            default:
                return null;
        }
    };

    // For published site: determine responsive device from CSS media queries
    // We generate CSS for all 3 breakpoints and use @media queries
    const responsiveCSS = `
        .landing-page-font-override {
            font-family: '${typography.bodyFont}', sans-serif !important;
            font-weight: ${typography.bodyWeight} !important;
        }
        .landing-page-font-override h1,
        .landing-page-font-override h2,
        .landing-page-font-override h3,
        .landing-page-font-override h4 {
            font-family: '${typography.headingFont}', sans-serif !important;
            font-weight: ${typography.headingWeight} !important;
        }
        ${typography.customSizes ? `
        /* Mobile first */
        .typo-heading { font-size: ${typography.sizes.mobile.heading}px !important; }
        .typo-subheading { font-size: ${typography.sizes.mobile.subheading}px !important; }
        .typo-body { font-size: ${typography.sizes.mobile.body}px !important; }
        .typo-small { font-size: ${typography.sizes.mobile.small}px !important; }

        @media (min-width: 768px) {
            .typo-heading { font-size: ${typography.sizes.tablet.heading}px !important; }
            .typo-subheading { font-size: ${typography.sizes.tablet.subheading}px !important; }
            .typo-body { font-size: ${typography.sizes.tablet.body}px !important; }
            .typo-small { font-size: ${typography.sizes.tablet.small}px !important; }
        }

        @media (min-width: 1024px) {
            .typo-heading { font-size: ${typography.sizes.desktop.heading}px !important; }
            .typo-subheading { font-size: ${typography.sizes.desktop.subheading}px !important; }
            .typo-body { font-size: ${typography.sizes.desktop.body}px !important; }
            .typo-small { font-size: ${typography.sizes.desktop.small}px !important; }
        }
        ` : ''}
    `;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: responsiveCSS }} />
            <div
                style={{ fontFamily: `'${typography.bodyFont}', sans-serif` }}
                className="min-h-screen w-full overflow-x-hidden bg-white landing-page-font-override @container"
            >
                {visibleSections.map((section) => {
                    if (!section.visible) return null;
                    return renderSection(section);
                })}
            </div>
        </>
    );
}
